import { z } from 'zod'

export interface LoanErrorMessage {
    fieldName: string
    message: string
}

export enum LoanName {
    CarFinanceBroker = 'Car Finance Broker',
    GuarantorLoan = 'Guarantor Loan',
    HirePurchase = 'Hire Purchase',
    PersonalLoan = 'Personal Loan',
    SecuredLoan = 'Secured Loan',
    CarLoan = 'Car Loan',
}

export enum AlternativeProductsDetailsCategoryType {
    AlternativeProduct = 'AlternativeProduct',
    CreditCardsCrossSell = 'CreditCardsCrossSell',
    Drafty = 'Draft',
    LowerLoanAmount = 'LowerLoanAmount',
}

export enum ScoredLoanScoreType {
    Bespoke = 'Bespoke',
    CarPreApproved = 'CarPreApproved',
    NotRated = 'NotRated',
    PreApproved = 'PreApproved',
    RateApproved = 'RateApproved',
    Rated = 'Rated',
    Sponsored = 'Sponsored',
}

export enum ScoringStatus {
    ScoresExpired = 'ScoresExpired',
    ServerError = 'ServerError',
    Success = 'Success',
    UserNotFound = 'UserNotFound',
    ValidationError = 'ValidationError',
}

export enum SecuredLeadResponseStatus {
    ServerError = 'ServerError',
    Success = 'Success',
    ValidationError = 'ValidationError',
}

export enum LoanProductCategory {
    AlternativeProducts = 'AlternativeProducts',
    CarFinance = 'CarFinance',
    Guarantor = 'Guarantor',
    Personal = 'Personal',
    PoorCredit = 'PoorCredit',
    Secured = 'Secured',
}

export enum CardsDownsellScoreStatus {
    NotRated = 70,
    Scored = 80,
    PreApprovedProduct = 90,
    PreApprovedRate = 100,
}

export enum LoanType {
    Guarantor = 'Guarantor',
    Personal = 'Personal',
    Secured = 'Secured',
    Unsecured = 'Unsecured',
}

export enum InterestRateType {
    Fixed = 'fixed',
    Variable = 'variable',
}

export interface ClickQuery {
    [key: string]: string
    loanType: string
    provider: string
    scoreId: string
    userId: string
    productId: string
}

export interface ScoredLoanDetails {
    acceptanceRate: number
    activeCategories: string[]
    additionalOfferDescription?: string
    additionalOfferTitle?: string
    annualInterestRate?: number
    apr: number
    arrangementFee?: number
    arrangementFeePercentage?: number
    broker?: string
    carModelType?: string
    ccdAnnualInterestRate?: number
    ccdAprFixedVariable?: number
    ccdArrangementFee?: number
    ccdAssumedBorrowing?: number
    ccdLoanTerm?: number
    ccdMonthlyPayment?: number
    ccdTotalRepayable?: number
    enableSecureToken?: boolean
    exclusiveLoan?: boolean
    hasAdditionalOffer: boolean
    hasOffer: boolean
    imageUrl: string
    interestRateType: InterestRateType
    legalName?: string
    lender: string
    loanId: string
    loanName: string
    loanType?: string
    maxAmount: number
    maxTerm: number
    minAmount: number
    minTerm: number
    offerDescription?: string
    offerTitle?: string
    propositionId: string
    propositionName?: string
    rateApproved?: boolean
    representativeExample?: string
    type: LoanType
    typicalApr: number
    relatedProductCodes?: string
    excludeFromCarPurchase?: boolean
}

export interface OpenBankingMetaData {
    isOpenBankingOffer?: boolean
    scoreInsights?: string[]
}

export interface ScoredLoan {
    activeCategories: string[]
    annualInterestRate?: number
    apr: number
    arrangementFee?: number
    clickQuery: ClickQuery
    clickUrl: string
    counterOffer: boolean
    exclusiveLoan: boolean
    finalPayment?: number
    firstPayment?: number
    ignoreThirdPartyReference: boolean
    imageUrl: string
    interestRateType: InterestRateType
    legalName?: string
    lenderName: string
    loanAmount: number
    loanId: string
    loanTerm: number
    monthlyPayment?: number
    openBankingMetaData: OpenBankingMetaData
    optionToPurchaseFee?: number
    productDetails: ScoredLoanDetails
    propositionId: string
    providerName: string
    rateApproved: boolean
    recommenderScore: number
    score?: number
    scoreType: ScoredLoanScoreType
    sponsoredMessage?: string
    totalCharge?: number
    totalRepayable?: number
    trueRate: boolean
    windex?: number
    isOpenBankingOffer?: boolean
}

export const ScoredExperimentVariantKeysSchema = z.union([
    z.literal('loan_ranking_productrecommender'),
    z.literal('loan_ranking_hierarchical'),
    z.literal('control'),
])

type VariantKeys = z.infer<typeof ScoredExperimentVariantKeysSchema>

interface ScoredExperimentMatchFactorRankingLoans {
    name: 'experiment_useMatchFactorRankingLoans'
    variant: VariantKeys
}

export type ScoredExperimentLoans = ScoredExperimentMatchFactorRankingLoans
export type ScoredExperimentLoansArray =
    | ScoredExperimentLoans[]
    | []
    | undefined

export interface ScoredLoansForm {
    category?: LoanProductCategory
    leadId: string
    loanAmount: number
    loanTerm: number
}

export interface LoansTableFiltersForm {
    loanAmount: number
    loanTerm: number
}

export interface FetchScoredLoansByScoreId {
    category?: LoanProductCategory
    scoreId: string
}

export interface FetchScoredLoansByScoreIdRequest {
    category?: string
    customerId: string
    customerType?: string
    dailyId?: string
    permanentId?: string
    scoreId: string
    visitGuid?: string
}

export interface FetchScoredLoansByFormRequest {
    customerId: string
    customerType?: string
    dailyId?: string
    leadId: string
    loanAmount: number
    loanTerm: number
    permanentId?: string
    visitGuid?: string
}

export interface GetScoredLoansResponse {
    errorMessages?: LoanErrorMessage[]
    experiments: ScoredExperimentLoans[]
    leadId: string
    loanAmount: number
    loanTerm: number
    scores: ScoredLoan[]
    status: ScoringStatus
    thirdPartyReference: string
}

export interface AlternativeProduct {
    apr: string
    categoryType: AlternativeProductsDetailsCategoryType
    cons: string[]
    cta: string
    description: string
    heading: string
    imageUrl: string
    name: string
    productId: string
    productType: AlternativeProductsDetailsCategoryType
    pros: string[]
    queryParams: Record<string, string>
}

export interface AlternativeProductCategory {
    category: string
    categoryName?: string
    description: string
    productCount: number
    products: AlternativeProduct[]
}

export interface FetchAlternativeProductsRequest {
    customerId: string
    customerType: string
    dailyId?: string
    permanentId?: string
    scoreId: string
    visitGuid?: string
}

export interface PostScoredAlternativeProductsResponse {
    categories: AlternativeProductCategory[]
}

export interface CardsDownsellProduct {
    annualFee?: number
    imageUrlLarge: string
    likelihood?: number
    maxCreditLimit?: number
    name: string
    productCode: string
    purchaseAER?: number
    representativeAPR?: number
    representativeExampleCreditLimit?: number
    score: number
    status: CardsDownsellScoreStatus
    tmScore?: number
    tmScoreByCreditCard?: number
}

export interface CardsDownsellProductsRequest {
    customerId: string
    dailyId?: string
    permanentId?: string
    scoreId: string
    visitGuid?: string
}

export interface PostCardsDownsellProductsResponse {
    categoryType: AlternativeProductsDetailsCategoryType
    cons?: string[]
    cta: string
    description?: string
    heading: string
    name: string
    productId: string
    productType: AlternativeProductsDetailsCategoryType
    products: CardsDownsellProduct[]
    pros?: string[]
    queryParams: { [key: string]: string }
}

export interface PostSecuredLeadRequest {
    apiClientName: string
    customerId: string
    dailyId?: string
    leadId: string
    loanAmount?: number
    loanId: string
    loanTerm?: number
    permanentId?: string
    phoneNumber: string
    productId: string
    propositionId: string
    row?: number
    visitGuid?: string
}

export interface PostSecuredLeadResponse {
    brokerName: string
    errorMessages: LoanErrorMessage[]
    status: SecuredLeadResponseStatus
}

export interface PostSecuredLeadDataResponse {
    phoneNumber: string
    errorMessages?: string[]
}

export interface PostCarFinanceApplicationRequest {
    clickId: string
    customerId: string
    dailyId?: string
    leadId: string
    loanAmount?: number
    loanTerm?: number
    permanentId?: string
    productId?: string
    provider?: string
    providerId?: string
    quoteId?: string
    ref?: string
    visitGuid?: string
}

export type PostCarFinanceApplicationResponse = boolean

export type LoansFormVariant = 'loans' | 'carFinance' | undefined

// Not from app
export interface SessionGuids {
    visitGuid: string
    dailyId: string
    permanentId: string
}

export type GetUnscoredLoansResponse = UnscoredLoanProduct[]

export interface UnscoredLoanProduct {
    loanId: string
    propositionId: string
    lender: string
    loanName: string
    propositionName: string
    imageUrl: string
    type: LoanType
    minAmount?: number
    maxAmount?: number
    minTerm?: number
    maxTerm?: number
    apr: number
    broker: string
    typicalApr?: number
    ccdAnnualInterestRate?: number
    ccdAprFixedVariable?: number
    ccdArrangementFee?: number
    ccdTotalRepayable?: number
    ccdLoanTerm?: number
    ccdMonthlyPayment?: number
    ccdAssumedBorrowing?: number
    arrangementFee?: number
    arrangementFeePercentage?: number
    acceptanceRate?: number
    annualInterestRate?: number
    exclusiveLoan?: boolean
    monthlyPayment?: number
    totalCharge?: number
    activeCategories?: string[]
    interestRateType: InterestRateType
    representativeExample: string
    hasOffer: boolean
    offerTitle: string
    offerDescription: string
    enableSecureToken?: boolean
    loanType: LoanType
    legalName: string
    carModelType: string
    totalRepayable?: number

    hasAdditionalOffer: boolean
    additionalOfferTitle: string
    additionalOfferDescription: string

    relatedProductCodes: string
    excludeFromCarPurchase: boolean
}

export type GetProductsResponse = UnscoredLoanProduct[]

export type GetAlternativeProductsResponse = AlternativeProduct[]

// Custom api

export interface UnscoredLoanProductsCategory {
    loans: UnscoredLoanProduct[]
    displayName: string
    id: LoanProductCategory
    templateId: LoanProductCategory
    totalLoans: number
}

export interface AlternativeProductsCategory {
    alternativeProducts: AlternativeProduct[]
    displayName: string
    id: LoanProductCategory.AlternativeProducts
    templateId: LoanProductCategory.AlternativeProducts
    totalProducts: number
}

export interface GetUnscoredCategoriesFilter {
    loanAmount: number
    loanTerm: number
}

export interface GetUnscoredCategoriesResponse {
    unscoredCategories: UnscoredLoanProductsCategory[]
    alternativeProducts: AlternativeProduct[]
    filter: GetUnscoredCategoriesFilter
}

export enum LoanPurpose {
    ConsolidateOtherDebts = 'Consolidate Other Debts',
    Holiday = 'Holiday',
    HomeImprovement = 'Home Improvement',
    VehiclePurchase = 'Vehicle Purchase',
    Wedding = 'Wedding',
    Other = 'Other',
}
